import { Badge, Button, Label, Switch } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Save, Share2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cfMessageKeys, useUpsertUtilitySettings } from "../api/queries";
import type { MessengerUtilitySettings } from "../api/types";

type Props = {
  pageId: string;
  settings: MessengerUtilitySettings;
};

function statusBadgeVariant(
  status: string | null,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "APPROVED") return "default";
  if (status === "REJECTED") return "destructive";
  if (status === "PENDING") return "secondary";
  return "outline";
}

export function UtilityTemplateSettingsCard({ pageId, settings }: Props) {
  const [templateName, setTemplateName] = useState(
    settings.paymentReminderTemplateName ?? "",
  );
  const [orderStatusTemplateName, setOrderStatusTemplateName] = useState(
    settings.orderStatusTemplateName ?? "",
  );
  const [deliveryStatusTemplateName, setDeliveryStatusTemplateName] = useState(
    settings.deliveryStatusTemplateName ?? "",
  );
  const [deliveryArtifactTemplateName, setDeliveryArtifactTemplateName] =
    useState(settings.deliveryArtifactTemplateName ?? "");
  const [languageCode, setLanguageCode] = useState(settings.languageCode);
  const [templatesReady, setTemplatesReady] = useState(settings.templatesReady);
  const save = useUpsertUtilitySettings(pageId);
  const qc = useQueryClient();

  useEffect(() => {
    setTemplateName(settings.paymentReminderTemplateName ?? "");
    setOrderStatusTemplateName(settings.orderStatusTemplateName ?? "");
    setDeliveryStatusTemplateName(settings.deliveryStatusTemplateName ?? "");
    setDeliveryArtifactTemplateName(
      settings.deliveryArtifactTemplateName ?? "",
    );
    setLanguageCode(settings.languageCode);
    setTemplatesReady(settings.templatesReady);
  }, [
    settings.paymentReminderTemplateName,
    settings.orderStatusTemplateName,
    settings.deliveryStatusTemplateName,
    settings.deliveryArtifactTemplateName,
    settings.languageCode,
    settings.templatesReady,
  ]);

  const dirty =
    (templateName.trim() || null) !==
      (settings.paymentReminderTemplateName ?? null) ||
    (orderStatusTemplateName.trim() || null) !==
      (settings.orderStatusTemplateName ?? null) ||
    (deliveryStatusTemplateName.trim() || null) !==
      (settings.deliveryStatusTemplateName ?? null) ||
    (deliveryArtifactTemplateName.trim() || null) !==
      (settings.deliveryArtifactTemplateName ?? null) ||
    languageCode.trim() !== settings.languageCode ||
    templatesReady !== settings.templatesReady;

  const liveMatchedStatus = useMemo(() => {
    const name = templateName.trim();
    if (!name) return null;
    const lang = languageCode.trim().toLowerCase();
    const exact = settings.metaTemplates.find(
      (row) => row.name === name && row.language.trim().toLowerCase() === lang,
    );
    if (exact) return exact.status;
    return (
      settings.metaTemplates.find((row) => row.name === name)?.status ?? null
    );
  }, [templateName, languageCode, settings.metaTemplates]);

  const blockEnableReady =
    liveMatchedStatus === "PENDING" || liveMatchedStatus === "REJECTED";

  return (
    <div className="mb-4 rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="inline-flex flex-wrap items-center gap-2">
            <h2 className="font-medium text-base">
              Meta Utility (ນອກ 24 ຊົ່ວໂມງ)
            </h2>
            {settings.isDefault ? (
              <Badge variant="secondary">env fallback</Badge>
            ) : (
              <Badge variant="outline">Page</Badge>
            )}
            {liveMatchedStatus ? (
              <Badge variant={statusBadgeVariant(liveMatchedStatus)}>
                Meta: {liveMatchedStatus}
              </Badge>
            ) : null}
          </div>
          <p className="max-w-2xl text-muted-foreground text-sm">
            ເປີດໜ້ານີ້ຈະດຶງລາຍການຈາກ Facebook{" "}
            <span className="font-mono text-foreground">
              GET /message_templates
            </span>
            . ຂໍ້ຄວາມທວງຍອດດ້ານລຸ່ມເຂົ້າ{" "}
            <span className="font-mono text-foreground">{"{{2}}"}</span> ເມື່ອສົ່ງ
            utility.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              qc.invalidateQueries({
                queryKey: cfMessageKeys.templates(pageId),
              })
            }
          >
            <RefreshCw className="size-4" aria-hidden />
            ດຶງຈາກ Meta
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate({
                paymentReminderTemplateName: templateName.trim() || null,
                orderStatusTemplateName: orderStatusTemplateName.trim() || null,
                deliveryStatusTemplateName:
                  deliveryStatusTemplateName.trim() || null,
                deliveryArtifactTemplateName:
                  deliveryArtifactTemplateName.trim() || null,
                languageCode: languageCode.trim() || "lo",
                templatesReady,
              })
            }
          >
            <Save className="size-4" aria-hidden />
            ບັນທຶກ utility
          </Button>
        </div>
      </div>

      {settings.metaFetchErrorCode === "FACEBOOK_PAGE_RECONNECT_REQUIRED" ? (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-3 text-destructive sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="font-medium text-sm">Facebook Page ຕ້ອງເຊື່ອມໃໝ່</p>
            <p className="text-xs">
              Token ໝົດອາຍຸ ຫຼື ສິດຂອງ Page ຖືກຖອນ. ເຊື່ອມ Facebook ໃໝ່ ແລ້ວກັບມາດຶງ Meta
              template ອີກຄັ້ງ.
            </p>
          </div>
          <Button asChild type="button" size="sm" className="shrink-0">
            <Link
              to="/app/facebook"
              search={{
                facebook: undefined,
                reason: undefined,
                pages: undefined,
                missing: undefined,
                omitted: undefined,
              }}
            >
              <Share2 className="size-4" aria-hidden />
              ໄປເຊື່ອມ Facebook
            </Link>
          </Button>
        </div>
      ) : settings.metaFetchError ? (
        <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-xs">
          ດຶງ Meta ບໍ່ສຳເລັດ: {settings.metaFetchError}
        </p>
      ) : null}

      {settings.metaTemplates.length > 0 ? (
        <div className="mt-4 space-y-2">
          <p className="text-muted-foreground text-xs">
            Template ບົນ Page ({settings.metaTemplates.length}) — ກົດເພື່ອເລືອກ
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border p-2">
            {settings.metaTemplates.map((row) => {
              const selected =
                row.name === templateName.trim() &&
                row.language.trim().toLowerCase() ===
                  languageCode.trim().toLowerCase();
              return (
                <li key={`${row.name}:${row.language}:${row.id ?? ""}`}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted ${
                      selected ? "bg-muted" : ""
                    }`}
                    onClick={() => {
                      setTemplateName(row.name);
                      setLanguageCode(row.language);
                      if (row.status === "APPROVED") {
                        setTemplatesReady(true);
                      }
                    }}
                  >
                    <span className="min-w-0 truncate font-mono text-xs">
                      {row.name}
                      <span className="text-muted-foreground">
                        {" "}
                        / {row.language}
                      </span>
                    </span>
                    <Badge variant={statusBadgeVariant(row.status)}>
                      {row.status}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : !settings.metaFetchError ? (
        <p className="mt-3 text-muted-foreground text-xs">
          ຍັງບໍ່ພົບ utility template ໃນ Page ນີ້ບົນ Meta
        </p>
      ) : null}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`utility-template-name-${pageId}`}>
            ຊື່ Meta template (ທວງຍອດ)
          </Label>
          <input
            id={`utility-template-name-${pageId}`}
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="cf_payment_reminder_lo"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`utility-order-status-${pageId}`}>
            ຊື່ Meta template (ສະຖານະອໍເດີ້)
          </Label>
          <input
            id={`utility-order-status-${pageId}`}
            type="text"
            value={orderStatusTemplateName}
            onChange={(e) => setOrderStatusTemplateName(e.target.value)}
            placeholder="cf_order_status_lo"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`utility-delivery-status-${pageId}`}>
            ຊື່ Meta template (ສະຖານະຈັດສົ່ງ)
          </Label>
          <input
            id={`utility-delivery-status-${pageId}`}
            type="text"
            value={deliveryStatusTemplateName}
            onChange={(e) => setDeliveryStatusTemplateName(e.target.value)}
            placeholder="cf_delivery_status_lo"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`utility-delivery-artifact-${pageId}`}>
            ຊື່ Meta template (ບິນ/ຮູບ Tracking)
          </Label>
          <input
            id={`utility-delivery-artifact-${pageId}`}
            type="text"
            value={deliveryArtifactTemplateName}
            onChange={(e) => setDeliveryArtifactTemplateName(e.target.value)}
            placeholder="cf_delivery_artifact_lo"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`utility-lang-${pageId}`}>ລະຫັດພາສາ</Label>
          <input
            id={`utility-lang-${pageId}`}
            type="text"
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            placeholder="lo"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
          <div className="space-y-0.5">
            <Label htmlFor={`utility-ready-${pageId}`}>ພ້ອມສົ່ງ</Label>
            <p className="text-muted-foreground text-xs">
              ເປີດໄດ້ເມື່ອ Meta ເປັນ APPROVED
            </p>
          </div>
          <Switch
            id={`utility-ready-${pageId}`}
            checked={templatesReady}
            onCheckedChange={(checked) => {
              if (checked && blockEnableReady) return;
              setTemplatesReady(checked);
            }}
          />
        </div>
      </div>

      {settings.effective.templatesReady &&
      settings.effective.paymentReminderTemplateName &&
      settings.effective.orderStatusTemplateName &&
      settings.effective.deliveryStatusTemplateName &&
      settings.effective.deliveryArtifactTemplateName ? (
        <p className="mt-3 text-muted-foreground text-xs">
          ກຳລັງໃຊ້:{" "}
          <span className="font-mono text-foreground">
            {[
              settings.effective.paymentReminderTemplateName,
              settings.effective.orderStatusTemplateName,
              settings.effective.deliveryStatusTemplateName,
              settings.effective.deliveryArtifactTemplateName,
            ].join(" / ")}
          </span>{" "}
          / {settings.effective.languageCode} (
          {settings.effective.source ?? "env"})
        </p>
      ) : (
        <p className="mt-3 text-amber-700 text-xs dark:text-amber-400">
          ຍັງບໍ່ພ້ອມ utility — ທວງຍອດນອກ 24 ຊົ່ວໂມງຈະຖືກຂັດ (policy_blocked)
        </p>
      )}
    </div>
  );
}
